import React from 'react';
import { CheckCircle2, AlertTriangle, AlertOctagon, Clock } from 'lucide-react';
import { cn } from '@vehix/portal/lib/utils';
import type { HealthBannerSummary } from '@vehix/shared/types/recallSnapshots';

interface Props {
  summary: HealthBannerSummary;
  isLoading?: boolean;
}

const STATUS_CONFIG = {
  healthy: {
    label: 'Healthy',
    Icon: CheckCircle2,
    classes: 'border-emerald-500/40 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-100',
    iconClass: 'text-emerald-600 dark:text-emerald-400'
  },
  warning: {
    label: 'Degraded',
    Icon: AlertTriangle,
    classes: 'border-amber-500/40 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-100',
    iconClass: 'text-amber-600 dark:text-amber-400'
  },
  stale: {
    label: 'Stale',
    Icon: Clock,
    classes: 'border-amber-500/40 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-100',
    iconClass: 'text-amber-600 dark:text-amber-400'
  },
  critical: {
    label: 'Down',
    Icon: AlertOctagon,
    classes: 'border-red-500/50 bg-red-50 text-red-900 dark:bg-red-950/30 dark:text-red-100',
    iconClass: 'text-red-600 dark:text-red-400'
  }
} as const;

export const HealthBanner: React.FC<Props> = ({ summary, isLoading }) => {
  const cfg = STATUS_CONFIG[summary.overall_status];
  const Icon = cfg.Icon;

  const detail = isLoading
    ? 'Loading…'
    : `${summary.healthy} of ${summary.total_assets} (tenant×OEM) assets healthy` +
      ` · ${summary.stale} stale` +
      ` · ${summary.critical} critical` +
      ` · ${summary.warning} warning` +
      ` · ${summary.failed_runs_last_7d} failed run${summary.failed_runs_last_7d === 1 ? '' : 's'} in 7d`;

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-md border-l-4 px-4 py-3',
        cfg.classes
      )}
      role='status'
      aria-live='polite'
    >
      <Icon className={cn('h-5 w-5 flex-shrink-0', cfg.iconClass)} />
      <div className='min-w-0 flex-1'>
        <p className='text-sm font-semibold'>{cfg.label}</p>
        <p className='text-xs opacity-90 truncate'>{detail}</p>
      </div>
    </div>
  );
};

export default HealthBanner;
